import React, { useEffect, useState } from "react";
import { Pie, Bar } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import { useNavigate } from "react-router-dom";
import { useStateContext } from "../../contexts/ContextProvider";
import axiosClient from "../../axios-client";
import { toast } from "react-toastify";
import Loading from "../../components/Loading";
import HourLoading from "../../components/HourLoading";
import { Button } from "../../components";
import MySelect from "../../components/MySelect";
import { IoIosSearch } from "react-icons/io";
import Alert from "../../components/Alert";

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

const DashboardDepenses = () => {
    // === Données brutes ===
    const [depensesDash, setDepensesDash] = useState([]);
    const [errors, setErrors] = useState([]);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const { token, setToken, user } = useStateContext();
    const [scolaires, setScolaires] = useState([]);
    const [selectedScolaire, setSelectedScolaire] = useState(null);
    const [inputValueScolaire, setValueScolaire] = useState('');
    const [req, setReq] = useState({
        scolaire: '',
    });
    const getDepensesDash = () => {
        setLoading(true);
        axiosClient.get('/depensesDash?scolaire=' + req.scolaire)
            .then(response => {
                setDepensesDash(response.data.depensesDash);
                setLoading(false);
                setErrors(null);
            }).catch(err => {
                const response = err.response;
                if (response.status === 401) {
                    setToken(null);
                }
                else {
                    if (response.status === 422) {
                        setErrors(response.data.errors);
                        setLoading(false);
                        toast.error('Une erreur est survenue!', {
                            position: "top-right",
                            autoClose: 5000,
                            hideProgressBar: false,
                            closeOnClick: true,
                            pauseOnHover: true,
                            draggable: true,
                            progress: undefined,
                            theme: "light",
                        });
                    } else {
                        if (response.status === 403) {
                            navigate('/403');
                        } else {
                            if (response.status === 404) {
                                navigate('/404');
                            }
                            else {
                                navigate('/500');
                            }
                        }
                    }

                }
            })
    }

    useEffect(() => {
        getScolaires();
    }, []);
    const getScolaires = () => {
        axiosClient.get('/getScolaires')
            .then((data) => {
                setScolaires(data.data.scolaires);
            }).catch(err => {
                navigate('/500');
            })
    }
    const handleChangeScolaire = value => {
        setSelectedScolaire(value);
        setReq({ ...req, scolaire: value.id });
    }

    const handleInputChangeScolaire = value => {
        setValueScolaire(value);
    };

    // === Regrouper par catégorie ===
    const depensesParCategorie = depensesDash.reduce((acc, d) => {
        acc[d.categorie] = (acc[d.categorie] || 0) + d.montant;
        return acc;
    }, {});

    const totalGlobal = Object.values(depensesParCategorie).reduce((a, b) => a + b, 0);

    const pieData = {
        labels: Object.keys(depensesParCategorie),
        datasets: [
            {
                label: "Dépenses par catégorie",
                data: Object.values(depensesParCategorie),
                backgroundColor: [

                    "#007bff", "#28a745", "#ff7f0e", "#dc3545",
                    "#3399ff", "#5cd65c", "#ff9933", "#ff4d4d",
                    "#66b2ff", "#85e085", "#ffb366", "#ff6666",
                    "#99ccff", "#00cc66", "#ff6600", "#ff1a1a",
                    "#4da6ff", "#66ff99", "#ff8533", "#e60000",
                    "#1e90ff", "#33cc33", "#ffa64d", "#ff3333",
                    "#00bfff", "#00e673", "#ff751a", "#ff5c5c",
                ],

            },
        ],
    };

    const pieOptions = {
        plugins: {
            tooltip: {
                callbacks: {
                    label: function (context) {
                        const value = context.raw;
                        const percentage = ((value / totalGlobal) * 100).toFixed(2);
                        const label = context.label || "";
                        return `${label}: ${value.toLocaleString()} MRU (${percentage}%)`;
                    },
                },
            },
            legend: {
                position: "bottom",
            },
        },
    };

    // === Regrouper par mois ===
    const depensesParMois = depensesDash.reduce((acc, d) => {
        const date = `${d.mois}/${d.annee}`;
        acc[date] = (acc[date] || 0) + d.montant;
        return acc;
    }, {});

    const barData = {
        labels: Object.keys(depensesParMois),
        datasets: [
            {
                label: "Dépenses mensuelles",
                data: Object.values(depensesParMois),
                backgroundColor: "#36A2EB",
            },
        ],
    };

    return (
        <div className="flex flex-col">
            {errors &&
                Object.keys(errors).map(key => (
                    <Alert key={key} message={errors[key][0]} />
                ))
            }
            <div className="flex w-full gap-3 flex-wrap md:flex-nowrap lg:flex-nowrap xl:flex-nowrap justify-between bg-white rounded-2xl shadow-lg border mb-8 border-gray-100 p-6">
                <MySelect label="Année scolaire" labelAr="السنة الدراسية" placeholder="Scolaire..." cacheOptions defaultOptions value={selectedScolaire} getOptionLabel={e => e.code} getOptionValue={e => e.id} options={scolaires} onInputChange={handleInputChangeScolaire} onChange={handleChangeScolaire} />
                <div className='mt-8'>
                    <Button onClick={() => getDepensesDash()} color="bg-yellow-500" icon={<IoIosSearch />} size="20" text="Chercher" textColor="text-white" />
                </div>
            </div>
            {loading ? <HourLoading /> :
                <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/*Section search - Fixed with proper container */}



                    {/* Pie Chart Card */}
                    <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 dark:bg-gray-800 dark:border-gray-700">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">Répartition par catégorie</h2>
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        </div>
                        <div className="relative">
                            <Pie
                                data={pieData}
                                options={{
                                    ...pieOptions,
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    plugins: {
                                        legend: {
                                            position: 'bottom',
                                            labels: {
                                                padding: 20,
                                                usePointStyle: true,
                                                font: {
                                                    size: 12,
                                                    family: 'inter'
                                                },
                                                color: '#4B5563'
                                            }
                                        }
                                    }
                                }}
                                className="h-64"
                            />
                        </div>
                    </div>

                    {/* Bar Chart Card */}
                    <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 dark:bg-gray-800 dark:border-gray-700">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">Dépenses par mois</h2>
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        </div>
                        <div className="relative h-64">
                            <Bar
                                data={barData}
                                options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    plugins: {
                                        legend: {
                                            display: false
                                        }
                                    },
                                    scales: {
                                        y: {
                                            beginAtZero: true,
                                            grid: {
                                                color: 'rgba(0,0,0,0.05)'
                                            },
                                            ticks: {
                                                callback: function (value) {
                                                    return value + ' MRU';
                                                }
                                            }
                                        },
                                        x: {
                                            grid: {
                                                display: false
                                            }
                                        }
                                    }
                                }}
                            />
                        </div>
                    </div>

                    {/* Summary Table Card */}
                    <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 col-span-1 md:col-span-2 dark:bg-gray-800 dark:border-gray-700">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">Détails par catégorie</h2>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                                Total: {totalGlobal.toLocaleString()} MRU
                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full border-collapse">
                                <thead>
                                    <tr className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800">
                                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b border-gray-200 dark:border-gray-600">
                                            Catégorie
                                        </th>
                                        <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b border-gray-200 dark:border-gray-600">
                                            Montant total (MRU)
                                        </th>
                                        <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b border-gray-200 dark:border-gray-600">
                                            Pourcentage
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200 dark:bg-gray-800 dark:divide-gray-700">
                                    {Object.entries(depensesParCategorie).map(([categorie, montant]) => {
                                        const percentage = ((montant / totalGlobal) * 100).toFixed(2);
                                        return (
                                            <tr key={categorie} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150">
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-200 border-b border-gray-200 dark:border-gray-600">
                                                    {categorie}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200 text-right border-b border-gray-200 dark:border-gray-600">
                                                    {montant.toLocaleString()}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200 text-right border-b border-gray-200 dark:border-gray-600">
                                                    <div className="flex items-center justify-end">
                                                        <span className="mr-2">{percentage}%</span>
                                                        <div className="w-16 bg-gray-200 rounded-full h-2 dark:bg-gray-600">
                                                            <div
                                                                className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                                                                style={{ width: `${percentage}%` }}
                                                            ></div>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    {/* Total row */}
                                    <tr className="bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20">
                                        <td className="px-6 py-4 text-sm font-bold text-gray-900 dark:text-gray-100 border-t-2 border-blue-200 dark:border-blue-600">
                                            TOTAL
                                        </td>
                                        <td className="px-6 py-4 text-sm font-bold text-gray-900 dark:text-gray-100 text-right border-t-2 border-blue-200 dark:border-blue-600">
                                            {totalGlobal.toLocaleString()}
                                        </td>
                                        <td className="px-6 py-4 text-sm font-bold text-gray-900 dark:text-gray-100 text-right border-t-2 border-blue-200 dark:border-blue-600">
                                            <div className="flex items-center justify-end">
                                                <span className="mr-2">100.00%</span>
                                                <div className="w-16 bg-gray-200 rounded-full h-2 dark:bg-gray-600">
                                                    <div className="bg-green-500 h-2 rounded-full"></div>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

            }
        </div>
    );
};

export default DashboardDepenses;
